import React, { useState, useEffect } from "react";
import { Clock, TrendingUp, Gift, Zap, Heart } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useLocation } from "react-router-dom";
import { useModal } from "../contexts/ModalContext";

interface StickyTimerProps {
  page?: "revenue" | "productivity" | "retention";
  type?: "webinar" | "consultation";
  className?: string;
}

const StickyTimer: React.FC<StickyTimerProps> = ({
  page = "revenue",
  type = "consultation",
  className = "",
}) => {
  const [isVisible, setIsVisible] = useState(true);
  const [timeLeft, setTimeLeft] = useState({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0,
  });
  const { pathname } = useLocation();
  const { handleBookNowClick } = useModal();

  const getIcon = () => {
    if (type === "webinar") return Gift;

    switch (page) {
      case "revenue":
        return TrendingUp;
      case "productivity":
        return Zap;
      case "retention":
        return Heart;
      default:
        return Gift;
    }
  };

  useEffect(() => {
    if (pathname.startsWith('/thank-you')) {
      setIsVisible(false);
    } else {
      setIsVisible(true);
    }
  }, [pathname]);

  const IconComponent = getIcon();

  const getContent = () => {
    switch (page) {
      case "revenue":
        return {
          title: "Offer Expires in",
          subtitle: "Limited Time Only",
          buttonText: "Book Now",
        };
      case "productivity":
        return {
          title: "Time Savings",
          subtitle: "15+ Hours Saved Weekly",
          buttonText: "Book Now",
        };
      case "retention":
        return {
          title: "Customer Retention",
          subtitle: "4-5x Higher Retention",
          buttonText: "Book Now",
        };
      default:
        return {
          title: "Free Consultation",
          subtitle: "$99 Consultation",
          buttonText: "Book Now",
        };
    }
  };

  const content = getContent();

  useEffect(() => {
    // Reset timer to 15 minutes on every page load
    const resetEndTime = new Date(Date.now() + 15 * 60 * 1000).toISOString();
    
    const timer = setInterval(() => {
      const now = new Date().getTime();
      const end = new Date(resetEndTime).getTime();
      const distance = end - now;

      if (distance < 0) {
        setTimeLeft({ days: 0, hours: 0, minutes: 0, seconds: 0 });
        return;
      }

      const days = Math.floor(distance / (1000 * 60 * 60 * 24));
      const hours = Math.floor(
        (distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)
      );
      const minutes = Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((distance % (1000 * 60)) / 1000);

      setTimeLeft({ days, hours, minutes, seconds });
    }, 1000);

    return () => clearInterval(timer);
  }, []); // Empty dependency array ensures this runs only once on mount

  if (!isVisible) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ y: 100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 100, opacity: 0 }}
        transition={{ type: "spring", damping: 25, stiffness: 300 }}
        className={`fixed bottom-0 left-0 right-0 bg-black text-white z-50 shadow-lg border-t-2 border-red-700 ${className}`}
      >
        <div className="max-w-7xl mx-auto px-3 md:px-4 py-2 md:py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2 md:space-x-4 flex-1 min-w-0">
              <div className="flex items-center space-x-2">
                <IconComponent className="h-4 w-4 text-red-500" />
              </div>

              <div className="flex flex-col">
                <span className="text-sm font-medium text-white">
                  {content.title}
                </span>
                <span className="text-xs text-gray-300">
                  {content.subtitle}
                </span>
              </div>

              <div className="flex items-center space-x-2">
                <div className="bg-red-600 px-3 py-1 rounded-lg flex items-center space-x-2">
                  <Clock className="h-4 w-4 text-white" />
                  <span className="text-sm font-bold text-white">
                    {timeLeft.minutes.toString().padStart(2, "0")}:
                    {timeLeft.seconds.toString().padStart(2, "0")}
                  </span>
                </div>
              </div>
            </div>

            {type === "webinar" ? (
              <div className="flex items-center space-x-2 flex-shrink-0">
                <button
                  onClick={() => window.open('https://rzp.io/rzp/are-webinar-india', '_blank')}
                  className="bg-red-600 hover:bg-red-700 text-white px-6 py-2 rounded-lg font-semibold transition-colors"
                >
                  Pay Now
                </button>
              </div>
            ) : (
              <div className="flex items-center space-x-2 flex-shrink-0">
                <button
                  onClick={handleBookNowClick}
                  className="bg-red-600 hover:bg-red-700 text-white px-6 py-2 rounded-lg font-semibold transition-colors"
                >
                  {content.buttonText}
                </button>
                <button
                  onClick={() => setIsVisible(false)}
                  className="text-gray-400 hover:text-white transition-colors p-1"
                >
                  {/* <X className="h-4 w-4" /> */}
                </button>
              </div>
            )}
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

export default StickyTimer;
